import React from 'react'
import { Card, Heading, Text } from '../ui'

/**
 * Props interface for the JourneyTimeline component
 */
export interface JourneyTimelineProps {
  /** Unique identifier for the section */
  id?: string
  /** CSS class name for custom styling */
  className?: string
}

/**
 * Career journey data extracted from resume experience
 */
const JOURNEY_DATA = [
  {
    id: 'current',
    period: '2016 - Present',
    title: 'Principal Software Architect',
    company: 'The Regis Company',
    description: `Leading enterprise architecture for platforms serving 100,000+ 
      concurrent users. Architecting cost-effective, scalable systems while 
      integrating cutting-edge LLM-powered DevOps workflows. This role represents 
      the culmination of everything I've learned about building systems that 
      matter at global scale.`,
  },
  {
    id: 'consulting',
    period: '2014 - Present',
    title: 'Lead Software Engineer',
    company: 'Eclosion Consulting',
    description: `Maintaining my consulting practice to stay connected with 
      emerging technologies and startup challenges. Building custom systems 
      for early-stage companies and advising on technology strategy. This 
      dual perspective keeps me grounded in both enterprise scale and 
      startup agility.`,
  },
  {
    id: 'games',
    period: '2012 - 2016',
    title: 'Network Engineer & Mobile Developer',
    company: 'Idol Minds & Aeria Games',
    description: `Transitioning into large-scale multiplayer systems and mobile 
      gaming. Built real-time backend services supporting thousands of concurrent 
      players while learning the unique challenges of global audiences with 
      diverse connectivity and device capabilities.`,
  },
  {
    id: 'early',
    period: '2009 - 2012',
    title: 'Software Engineer & Game Developer',
    company: 'Black Lantern Studios & Artemis Vision',
    description: `My journey began in the high-pressure world of game development, 
      where performance isn't optional—it's survival. Building games for Nintendo DS 
      and exploring machine vision taught me to optimize ruthlessly while maintaining 
      code quality. This foundation shaped everything that followed.`,
  },
] as const

/**
 * Journey timeline component showcasing career progression and key experiences.
 *
 * Features:
 * - Visual timeline with career milestones
 * - Chronological progression from early career to present
 * - Accessible structure with proper ARIA labels
 * - Responsive design that works on mobile devices
 * - Semantic HTML with timeline semantics
 */
export const JourneyTimeline: React.FC<JourneyTimelineProps> = ({
  id,
  className = '',
}) => {
  return (
    <section
      className={`about-section ${className}`}
      aria-labelledby="journey-heading"
      id={id}
    >
      <Heading
        as="h2"
        size="xl"
        variant="default"
        className="section-title"
        id="journey-heading"
      >
        My Journey
      </Heading>

      <Card variant="surface" padding="lg">
        <div
          className="timeline-container"
          role="list"
          aria-label="Career timeline"
        >
          <div className="timeline-line" aria-hidden="true" />

          {JOURNEY_DATA.map((item, index) => (
            <div
              key={item.id}
              className="timeline-item"
              role="listitem"
              aria-label={`Career milestone ${index + 1} of ${JOURNEY_DATA.length}`}
            >
              <div className="timeline-marker" aria-hidden="true" />
              <div className="timeline-content">
                <div className="timeline-period">{item.period}</div>
                <Heading
                  as="h3"
                  size="lg"
                  variant="default"
                  className="timeline-title"
                >
                  {item.title}
                </Heading>
                <Text
                  size="sm"
                  variant="secondary"
                  className="timeline-company"
                >
                  {item.company}
                </Text>
                <Text
                  size="base"
                  variant="primary"
                  className="timeline-description"
                >
                  {item.description}
                </Text>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </section>
  )
}
